import {EventEmitter, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {map} from 'rxjs/operators';

const REGEX_TOKEN = /{(.+?)}/g;

export interface IRemoteTranslation {
  key: string;
  value: string;
}

@Injectable()
export class TranslationService {
  public languageChanged: Observable<string>;

  private _registeredLanguagesAndTranslations = new Map<string, Map<string, string>>();
  private _languageChanged: EventEmitter<string> = new EventEmitter<string>();

  private _languageCode: string;
  private _translations: Map<string, string>;
  private _fallbackTranslations: Map<string, string>;
  private _fallbackLanguageCode: string;
  private _asyncTranslations: Map<string, Map<string, string>> = new Map();
  private _translationCache = new ReplaySubject<Map<string, Map<string, string>>>(1);


  public constructor() {
    this.languageChanged = this._languageChanged.asObservable();
  }

  public addTranslations(language: string, obj: any): void {
    let translations = this._registeredLanguagesAndTranslations[language];
    if (!translations) {
      this._registeredLanguagesAndTranslations[language] = translations = new Map<string, string>();
    }
    this._addRecursively(translations, obj, '');
  }

  public translate(key: string, tokens?: Object): string {
    let translation = key;
    if (this._isValidTranslation(this._translations, key)) {
      translation = this._translations[key];
    } else if (this._isValidTranslation(this._fallbackTranslations, key)) {
      translation = this._fallbackTranslations[key];
    }
    return this._replaceTokens(translation, tokens);
  }

  public translateAsync(value: string): Observable<string> {
    return this._translationCache.pipe(map(translations => translations.get(this._languageCode).get(value)));
  }

  public translateDefault(key: string, defaultValue: string, tokens?: Object): string {
    const translation = this._translations && this._translations[key]
        || this._fallbackTranslations && this._fallbackTranslations[key]
        || defaultValue;

    return this._replaceTokens(translation, tokens);
  }

  public useLanguage(primary: string, fallback?: string): void {
    if (primary && primary !== this._languageCode) {
      const translations = this._registeredLanguagesAndTranslations[primary];

      if (translations) {
        this._translations = translations;
        this._languageCode = primary;

        this._languageChanged.emit(primary);
      }
    }

    this.useFallbackLanguage(fallback);
  }

  public useFallbackLanguage(fallback: string): void {
    if (fallback && fallback !== this._fallbackLanguageCode) {
      const translations = this._registeredLanguagesAndTranslations[fallback];

      if (translations) {
        this._fallbackTranslations = translations;
        this._fallbackLanguageCode = fallback;
      }
    }
  }

  public getLanguage(): string {
    return this._languageCode;
  }

  private _isValidTranslation(translation: Map<string, string>, key: string): boolean {
    if (translation && (translation[key] || translation[key] === '')) {
      return true;
    }
    return false;
  }

  private _addRecursively(translations: Map<string, string>, obj: any, prefix: string): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        if (typeof value === 'string') {
          translations[prefix + key] = value;
        } else {
          this._addRecursively(translations, value, prefix + key + '.');
        }
      }
    }
  }

  private _replaceTokens(translation: string, tokens?: Object): string {
    if (tokens) {
      translation = translation.replace(REGEX_TOKEN, (match, key) => tokens[key] || key);
    }

    return translation;
  }
}
